import { U21SelectValue } from 'app/shared/u21-ui/components/input/select/U21Select';

import { ForwardedRef, forwardRef } from 'react';
import { alpha } from '@mui/system/colorManipulator';
import useForkRef from '@mui/utils/useForkRef';
import styled from 'styled-components';

import { StyledU21Checkbox } from 'app/shared/u21-ui/components/input/select/SelectOptionItem';
import { MultiSelectListboxProps } from 'app/shared/u21-ui/components/input/select/MultiSelectListbox';
import { U21Divider } from 'app/shared/u21-ui/components/display/U21Divider';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { U21Tooltip } from 'app/shared/u21-ui/components/display/U21Tooltip';
import { useListboxVirtualizer } from 'app/shared/u21-ui/components/input/select/useListboxVirtualizer';

export interface VirtualizedMultiSelectListboxProps<
  TValue extends U21SelectValue,
  TClearable extends boolean,
> extends MultiSelectListboxProps<TValue, TClearable> {
  estimateSize?: number;
  highlightedIndex?: number;
  overscan?: number;
}

const VirtualizedMultiSelectListboxInner = <
  TValue extends U21SelectValue,
  TClearable extends boolean,
>(
  props: VirtualizedMultiSelectListboxProps<TValue, TClearable>,
  ref: ForwardedRef<HTMLUListElement>,
) => {
  const {
    allSelected,
    children,
    estimateSize,
    hasDisabledSelectedOptions,
    hasDisabledUnselectedOptions,
    highlightedIndex = -1,
    overscan,
    selectAllTooltip = '',
    onChange,
    selectAllEnabled,
    selectableOptions,
    value,
    requiredValue,
    disabledSelectedValue,
    ...rest
  } = props;

  const { childrenArray, setListRef, virtualizer } = useListboxVirtualizer({
    children,
    estimateSize,
    highlightedIndex,
    overscan,
  });

  const mergedRef = useForkRef(ref, setListRef);

  const selectAllHeader = selectAllEnabled ? (
    <>
      <U21Tooltip tooltip={selectAllTooltip} placement="right">
        <SelectAllItem
          aria-selected={allSelected}
          className="MuiAutocomplete-option"
          onClick={(e) =>
            onChange(
              allSelected
                ? [...new Set([...requiredValue, ...disabledSelectedValue])]
                : [
                    ...new Set([
                      ...disabledSelectedValue,
                      ...selectableOptions
                        .map((i) => i.value)
                        .filter((i): i is TValue => i !== undefined),
                    ]),
                  ],
              e,
            )
          }
        >
          <U21Spacer horizontal>
            <StyledU21Checkbox
              checked={allSelected}
              indeterminate={!allSelected && value.length > 0}
              onChange={() => {}}
            />
            <U21Typography variant="body2">
              {(() => {
                if (allSelected) {
                  return hasDisabledSelectedOptions
                    ? 'Deselect all available'
                    : 'Deselect all';
                }
                return hasDisabledUnselectedOptions
                  ? 'Select all available'
                  : 'Select all';
              })()}
            </U21Typography>
          </U21Spacer>
        </SelectAllItem>
      </U21Tooltip>
      <StyledDivider horizontal />
    </>
  ) : null;

  return (
    <StyledMenu ref={mergedRef} {...rest}>
      {selectAllHeader}
      <StyledVirtualContainer $height={virtualizer.getTotalSize()}>
        {virtualizer.getVirtualItems().map((item) => (
          <VirtualItem
            key={item.key}
            ref={virtualizer.measureElement}
            data-index={item.index}
            $translateY={item.start}
            role="presentation"
          >
            {childrenArray[item.index]}
          </VirtualItem>
        ))}
      </StyledVirtualContainer>
    </StyledMenu>
  );
};

export const VirtualizedMultiSelectListbox = forwardRef(
  VirtualizedMultiSelectListboxInner,
);

const StyledMenu = styled.ul`
  padding: ${({ theme }) => theme.spacing(1)};
`;

const StyledVirtualContainer = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  position: relative;
`;

const VirtualItem = styled.div<{ $translateY: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  transform: translateY(${({ $translateY }) => $translateY}px);
`;

const SelectAllItem = styled.li`
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  &&& {
    min-height: 46px;
    padding: ${({ theme }) => theme.spacing(1)};
  }

  &:hover {
    background-color: ${(props) =>
      props.theme.palette.grey[
        props.theme.palette.mode === 'light' ? 300 : 500_8
      ]};
  }

  &[aria-selected='true'] {
    &:hover {
      background-color: ${(props) => {
        return alpha(
          props.theme.palette.primary.main,
          props.theme.palette.mode === 'light' ? 0.16 : 0.24,
        );
      }};
    }
  }
`;

const StyledDivider = styled(U21Divider)`
  margin: ${({ theme }) => theme.spacing(1)} 0;
`;
